import React, { useState, useEffect, useMemo, useRef } from "react";
import useApi from "../../hooks/useApiGetPost";

// Helper & Utility Imports
import useApiAthul from "../../hooks/useApi";
import groupSalesThreeLevels from "../utils/groupSalesThreeLevels";
import { exportToExcel } from "./exportToExcel";
import DynamicTableForStockReport from "./DynamicTableForStockReport";

// --- Lightweight Tailwind form primitives (Matched to InvoiceProfitAnalysisReport style) ---

const FieldLabel = ({ children }) => (
  <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
    {children}
  </label>
);

const TextInput = ({ className = "", ...props }) => (
  <input
    {...props}
    className={`w-full bg-white border border-gray-300 rounded-md px-2.5 py-1.5 text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300 ${className}`}
  />
);

const SelectInput = ({ className = "", children, ...props }) => (
  <select
    {...props}
    className={`w-full bg-white border border-gray-300 rounded-md px-2.5 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300 ${className}`}
  >
    {children}
  </select>
);

const Spinner = () => (
  <svg
    className="animate-spin h-4 w-4 text-gray-400"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
    />
  </svg>
);

// Minimal typeahead replacement matched to the report structure
function SimpleAutocomplete({
  label,
  options,
  getOptionLabel,
  value,
  onChange,
  loading,
  placeholder,
  width = "w-[200px]",
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setQuery(value ? getOptionLabel(value) || "" : "");
  }, [value]);

  const filtered = (options || []).filter((opt) =>
    (getOptionLabel(opt) || "").toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div className={`relative ${width}`}>
      <FieldLabel>{label}</FieldLabel>
      <div className="relative">
        <TextInput
          type="text"
          value={query}
          placeholder={placeholder || "Type to search..."}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
        />
        {loading && (
          <div className="absolute right-2 top-1/2 -translate-y-1/2">
            <Spinner />
          </div>
        )}
      </div>
      {open && filtered.length > 0 && (
        <div className="absolute z-20 mt-1 w-full max-h-48 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg">
          {filtered.map((opt, i) => (
            <div
              key={i}
              onMouseDown={() => {
                onChange(opt);
                setQuery(getOptionLabel(opt) || "");
                setOpen(false);
              }}
              className="px-3 py-1.5 text-sm text-gray-700 hover:bg-violet-50 cursor-pointer"
            >
              {getOptionLabel(opt)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// --- Utility Functions ---

const isNumericHeader = (header) =>
  [
    "AMT",
    "COMMISSION",
    "RETURN",
    "VAT",
    "TOTAL",
    "PROFIT",
    "COST",
    "QTY",
    "AMOUNT",
    "SRNO",
    "VAT_AMT",
    "VATAMT",
    "VAT AMT",
    "TOTAL_AMT",
    "GROSS SALE",
    "DISC",
    "NET SALES",
    "EXCL_VAT_AMOUNT",
  ].some((s) => header.toUpperCase().includes(s));

const getDescriptiveColumnKey = (mode, data) => {
  if (!data || data.length === 0) return "CODE";
  if (mode === "SALES_CUSTOMER") return "ACCOUNT";
  if (mode === "SALES_SALESMAN") return "SALESPERSON";

  const descriptiveKey = Object.keys(data[0]).find(
    (key) =>
      !isNumericHeader(key) &&
      !["isGroupHeader", "isSubHeader", "totalAmount"].includes(key)
  );

  return (
    descriptiveKey ||
    Object.keys(data[0]).find((key) => !isNumericHeader(key)) ||
    "CODE"
  );
};

const calculateGrandTotals = (data) => {
  if (!data || data.length === 0) return {};
  const totals = {};
  const firstRow = data[0];

  Object.keys(firstRow).forEach((key) => {
    if (typeof firstRow[key] === "number" || isNumericHeader(key)) {
      totals[key] = data.reduce((sum, row) => sum + Number(row[key] || 0), 0);
    }
  });
  return totals;
};

const useScrollLoading = (data) => {
  const defaultRowsPerPage = 1000;
  const [rowsToDisplay, setRowsToDisplay] = useState(defaultRowsPerPage);

  const loadNextPage = () => {
    setRowsToDisplay((prev) =>
      Math.min(prev + defaultRowsPerPage, data.length)
    );
  };

  useEffect(() => {
    setRowsToDisplay(data.length > 0 ? defaultRowsPerPage : 0);
  }, [data.length]);

  const paginatedRows = data.slice(0, rowsToDisplay);
  const hasMore = rowsToDisplay < data.length;

  return { paginatedRows, loadNextPage, hasMore, totalRows: data.length };
};

// --- Main StockReport Component ---

const StockReport = () => {
  const COMPANY_CODE = localStorage?.getItem("companyCode") || "-";
  let API_PATHS = localStorage.getItem("apiPaths")
    ? JSON.parse(localStorage.getItem("apiPaths"))
    : [{ API_CONFIG: "https://cubixweberp.com:213" }];

  const [isCallingViewPDFAPI, setIsCallingViewPDFAPI] = useState(false);
  const [isCallingDownloadPDFAPI, setIsCallingDownloadPDFAPI] = useState(false);
  const [showDownloadExcelLoader, setShowDownloadExcelLoader] = useState(false);
  const [currentAPIUrlForExcel, setCurrentAPIUrlForExcel] = useState(null);

  const [current_page_number, set_current_page_number] = useState(1);
  const [location_value_to_paas_to_api, setLocation_value_to_paas_to_api] =
    useState("-");

  const { request, loading } = useApiAthul();

  const [current_statusOfReturnCheckbox, setCurrentStatusOfReturnCheckbox] =
    useState("All");

  const [searchTextTable, setSearchTextTable] = useState("");

  const salesModes = [
    { label: "Inventory Value As on Date", value: "STK_MAIN_QTY_COST_ASON" },
  ];

  const [currentSelectedModeObject, setCurrentSelectedModeObject] =
    useState(null);
  const [currentSelectedDepartment, setCurrentSelectedDepartment] =
    useState("-");
  const [userDepartmentList, setUserDepartment] = useState(null);
  const [locationListFromAPI, setLocationListFromAPI] = useState(null);

  const [currentSelectedCategory, setCurrentSelectedCategory] = useState("-");
  const [currentSelectedSubCategory, setCurrentSelectedSubCategory] =
    useState("-");
  const [currentSelectedGroup, setCurrentSelectedGroup] = useState("-");
  const [currentSelectedSubGroup, setCurrentSelectedSubGroup] = useState("-");

  const {
    loading: loadinggetSubCategory,
    data: datagetSubCategory,
    get: getSubCategory,
  } = useApi();
  const {
    loading: loadinggetCategory,
    data: datagetCategory,
    get: getCategory,
  } = useApi();
  const {
    loading: loadinggetSubgroup,
    data: datagetSubgroup,
    get: getSubgroup,
  } = useApi();
  const {
    loading: loadinggetgroup,
    data: datagetgroup,
    get: getgroup,
  } = useApi();

  const {
    data: datagetSalesOrderReportAPI,
    loading: loadinggetSalesOrderReportAPI,
    get: getSalesOrderReportAPI,
  } = useApi();

  const {
    data: datagetTotalSeperateAPI,
    loading: loadinggetTotalSeperateAPI,
    get: getTotalSeperateAPI,
  } = useApi();

  const { data: datagetgetFinancialDateApi, get: getFinancialDateApi } =
    useApi();
  const { data: datagetLocationList, get: getLocationList } = useApi();

  const [isFilterPanelVisible, setIsFilterPanelVisible] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const scrollContainerRef = useRef(null);

  useEffect(() => {
    const currentUser = JSON.parse(localStorage.getItem("currentUser"));
    setUserDepartment(currentUser || null);

    setCurrentSelectedDepartment(
      currentUser?.[0]?.DEPTNO ? currentUser[0].DEPTNO : "-"
    );

    const today = new Date().toLocaleDateString("en-CA");
    setDateFrom(today);
    setDateTo(today);

    getCategory(
      `${API_PATHS[0].API_CONFIG}/api/mastercount/${COMPANY_CODE}/category/-/-`
    );
    getSubCategory(
      `${API_PATHS[0].API_CONFIG}/api/mastercount/${COMPANY_CODE}/subcategory/-/-`
    );
    getgroup(
      `${API_PATHS[0].API_CONFIG}/api/mastercount/${COMPANY_CODE}/group/-/-`
    );
    getSubgroup(
      `${API_PATHS[0].API_CONFIG}/api/mastercount/${COMPANY_CODE}/subgroup/-/-`
    );

    let departmentToPassToAPI = currentSelectedDepartment || "-";

    getLocationList(
      `${API_PATHS[0].API_CONFIG}/api/CpaysCount/${COMPANY_CODE}/LOCATONLIST_ALL/-/-`
    );

    handleChipClick({
      label: "Inventory Value As on Date",
      value: "STK_MAIN_QTY_COST_ASON",
    });
  }, []);

  useEffect(() => {
    if (datagetTotalSeperateAPI) {
      let mode_to_pass_to_api = currentSelectedModeObject
        ? currentSelectedModeObject.value
        : "-";

      if (location_value_to_paas_to_api !== "-") {
        mode_to_pass_to_api = "STK_LOC_QTY_COST_ASON";
      }

      let apiUrl = `${
        API_PATHS[0].API_CONFIG
      }/api/CRMStockReport/${COMPANY_CODE}/${mode_to_pass_to_api}/${dateTo}/${encodeURIComponent(
        currentSelectedGroup
      )}/${encodeURIComponent(currentSelectedSubGroup)}/${encodeURIComponent(
        currentSelectedCategory
      )}/${encodeURIComponent(
        currentSelectedSubCategory
      )}/${current_statusOfReturnCheckbox}/${location_value_to_paas_to_api}/1/${
        datagetTotalSeperateAPI[0].TotalRows
      }`;

      setCurrentAPIUrlForExcel(apiUrl);
    }
  }, [datagetTotalSeperateAPI]);

  const handleChipClick = (chip) => {
    setCurrentSelectedModeObject(chip);
  };

  const resetFilters = () => {
    if (datagetgetFinancialDateApi) {
      const fyStart = datagetgetFinancialDateApi[0]?.fystart?.split("T")[0];
      setDateFrom(fyStart);
    }

    const today = new Date().toLocaleDateString("en-CA");
    setDateTo(today);

    handleChipClick({
      label: "Inventory Value As on Date",
      value: "STK_MAIN_QTY_COST_ASON",
    });
    setCurrentSelectedGroup("-");
    setCurrentSelectedSubGroup("-");
    setCurrentSelectedCategory("-");
    setCurrentSelectedSubCategory("-");
    setLocation_value_to_paas_to_api("-");
  };

  const callStockReportAPI = (pageNumber) => {
    if (currentSelectedModeObject && dateTo && currentSelectedDepartment) {
      let mode_to_pass_to_api = currentSelectedModeObject
        ? currentSelectedModeObject.value
        : "-";

      let ret_status_for_api = current_statusOfReturnCheckbox;

      let apiUrlTotal = `${
        API_PATHS[0].API_CONFIG
      }/api/CRMStockReport/${COMPANY_CODE}/STK_MAIN_QTY_COST_ASON_TOTALS/${dateTo}/${encodeURIComponent(
        currentSelectedGroup
      )}/${encodeURIComponent(currentSelectedSubGroup)}/${encodeURIComponent(
        currentSelectedCategory
      )}/${encodeURIComponent(
        currentSelectedSubCategory
      )}/${ret_status_for_api}/${location_value_to_paas_to_api}/1/1000`;

      if (location_value_to_paas_to_api !== "-") {
        mode_to_pass_to_api = "STK_LOC_QTY_COST_ASON";
        apiUrlTotal = `${
          API_PATHS[0].API_CONFIG
        }/api/CRMStockReport/${COMPANY_CODE}/STK_LOC_QTY_COST_ASON/${dateTo}/${encodeURIComponent(
          currentSelectedGroup
        )}/${encodeURIComponent(currentSelectedSubGroup)}/${encodeURIComponent(
          currentSelectedCategory
        )}/${encodeURIComponent(
          currentSelectedSubCategory
        )}/TOTALS/${location_value_to_paas_to_api}/1/1000`;
      }

      let apiUrl = `${
        API_PATHS[0].API_CONFIG
      }/api/CRMStockReport/${COMPANY_CODE}/${mode_to_pass_to_api}/${dateTo}/${encodeURIComponent(
        currentSelectedGroup
      )}/${encodeURIComponent(currentSelectedSubGroup)}/${encodeURIComponent(
        currentSelectedCategory
      )}/${encodeURIComponent(
        currentSelectedSubCategory
      )}/${ret_status_for_api}/${location_value_to_paas_to_api}/${pageNumber}/1000`;

      getSalesOrderReportAPI(apiUrl);
      getTotalSeperateAPI(apiUrlTotal);
    }
  };

  useEffect(() => {
    set_current_page_number(1);
  }, [currentSelectedModeObject, dateTo, currentSelectedDepartment]);

  useEffect(() => {
    callStockReportAPI(current_page_number);
  }, [
    current_page_number,
    currentSelectedModeObject,
    dateTo,
    currentSelectedDepartment,
  ]);

  useEffect(() => {
    getFinancialDateApi(
      `${API_PATHS[0].API_CONFIG}/api/Cpayscount/${COMPANY_CODE}/companyinfo/-/-`
    );
  }, []);

  useEffect(() => {
    if (datagetgetFinancialDateApi) {
      const fyStart = datagetgetFinancialDateApi[0]?.fystart?.split("T")[0];
      setDateFrom(fyStart);
    }
  }, [datagetgetFinancialDateApi]);

  const getTotalColumns = (data) => {
    if (!data || data.length === 0) return 1;
    const allKeys = Object.keys(data[0]);
    const descriptiveColumnKey = getDescriptiveColumnKey(
      currentSelectedModeObject?.value,
      data
    );

    const excludedKeys = [
      "isGroupHeader",
      "isSubHeader",
      "isSubSubHeader",
      "totalAmount",
      "totalAmountExclVAT",
      descriptiveColumnKey,
    ];

    const displayKeys = allKeys.filter((key) => !excludedKeys.includes(key));
    return 1 + displayKeys.length;
  };

  const reportData = datagetSalesOrderReportAPI || [];

  const filteredRows = useMemo(() => {
    let finalData = reportData;
    if (searchTextTable) {
      const search = searchTextTable.toLowerCase();
      finalData = reportData.filter((row) =>
        Object.values(row).some((value) =>
          String(value).toLowerCase().includes(search)
        )
      );
    }
    return finalData;
  }, [reportData, currentSelectedModeObject, searchTextTable]);

  const keysToTotal = useMemo(() => {
    if (!reportData || reportData.length === 0) return [];
    const firstRow = reportData[0];
    const allKeys = Object.keys(firstRow);
    return allKeys.filter((key) => isNumericHeader(key));
  }, [reportData]);

  const descriptiveColumnKey = useMemo(() => {
    return getDescriptiveColumnKey(
      currentSelectedModeObject?.value,
      filteredRows
    );
  }, [currentSelectedModeObject, filteredRows]);

  const grandColumnTotals = useMemo(() => {
    return calculateGrandTotals(reportData);
  }, [reportData]);

  const CurrentTableComponent = currentSelectedModeObject
    ? DynamicTableForStockReport
    : null;

  const { paginatedRows, loadNextPage, hasMore, totalRows } =
    useScrollLoading(filteredRows);

  const handleScroll = () => {
    const currentTarget = scrollContainerRef.current;
    if (!currentTarget || loadinggetSalesOrderReportAPI || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = currentTarget;

    if (scrollHeight - scrollTop <= clientHeight + 10) {
      loadNextPage();
    }
  };

  const totalColumnsCount = getTotalColumns(paginatedRows);

  const handleDownload = async () => {
    try {
      setShowDownloadExcelLoader(true);
      const res = await fetch(currentAPIUrlForExcel);
      if (!res.ok) throw new Error("Failed to fetch data");
      const data = await res.json();

      exportToExcel(
        data,
        "Stock_Report.xlsx",
        datagetTotalSeperateAPI,
        currentSelectedModeObject,
        location_value_to_paas_to_api
      );

      setShowDownloadExcelLoader(false);
    } catch (err) {
      console.error(err);
      alert("Failed to download Excel. Please try again.");
    }
  };

  const downloadPDF = async () => {
    const compcode = COMPANY_CODE;
    let opmod = "STK_MAIN_QTY_COST_ASON";
    const group = encodeURIComponent(currentSelectedGroup);
    const sgroup = encodeURIComponent(currentSelectedSubGroup);
    const category = encodeURIComponent(currentSelectedCategory);
    const scategory = encodeURIComponent(currentSelectedSubCategory);
    const filter = current_statusOfReturnCheckbox;
    const locn = location_value_to_paas_to_api;
    const pageNo = "1";
    const pageSize = datagetTotalSeperateAPI[0].TotalRows + 1;

    if (location_value_to_paas_to_api !== "-") {
      opmod = "STK_LOC_QTY_COST_ASON";
    }

    const url = `${API_PATHS[0].API_CONFIG}/api/CRMStockReportPdf/${compcode}/${opmod}/${dateTo}/${group}/${sgroup}/${category}/${scategory}/${filter}/${locn}/${pageNo}/${pageSize}`;

    try {
      setIsCallingDownloadPDFAPI(true);
      const response = await fetch(url, { method: "GET" });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || "Failed to fetch report");
      }
      const blob = await response.blob();
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `${opmod}.pdf`;
      link.click();
      window.URL.revokeObjectURL(link.href);
      setIsCallingDownloadPDFAPI(false);
    } catch (error) {
      setIsCallingDownloadPDFAPI(false);
      console.error("Error downloading PDF:", error);
      alert("Failed to download report");
    }
  };

  const ViewPDF = async () => {
    const compcode = COMPANY_CODE;
    let opmod = "STK_MAIN_QTY_COST_ASON";
    const group = encodeURIComponent(currentSelectedGroup);
    const sgroup = encodeURIComponent(currentSelectedSubGroup);
    const category = encodeURIComponent(currentSelectedCategory);
    const scategory = encodeURIComponent(currentSelectedSubCategory);
    const filter = current_statusOfReturnCheckbox;
    const locn = location_value_to_paas_to_api;
    const pageNo = "1";
    const pageSize = datagetTotalSeperateAPI[0].TotalRows + 1;

    if (location_value_to_paas_to_api !== "-") {
      opmod = "STK_LOC_QTY_COST_ASON";
    }

    const url = `${API_PATHS[0].API_CONFIG}/api/CRMStockReportPdf/${compcode}/${opmod}/${dateTo}/${group}/${sgroup}/${category}/${scategory}/${filter}/${locn}/${pageNo}/${pageSize}`;

    setIsCallingViewPDFAPI(true);
    const response = await fetch(url);
    const blob = await response.blob();
    setIsCallingViewPDFAPI(false);

    const pdfUrl = window.URL.createObjectURL(blob);
    window.open(pdfUrl, "_blank");
  };

  const max_date_today = new Date().toISOString().split("T")[0];

  return (
    <div
      className="bg-gray-200 overflow-hidden"
      style={{ height: "calc(100vh - 50px)" }}
    >
      <div className="max-w-full mx-auto bg-white shadow-xl rounded-none border border-violet-100 flex flex-col h-full overflow-hidden">
        {/* Report Header */}
        <div className="border-b border-violet-100 px-6 py-4 bg-gradient-to-r from-violet-50 via-white to-sky-50">
          <div className="flex items-center justify-between">
            <h1
              style={{
                fontSize: "18px",
                fontWeight: 500,
                color: "#3730a3",
                textTransform: "uppercase",
              }}
              className="tracking-wide"
            >
              Stock Report
            </h1>
            <div className="flex gap-2">
              {isCallingViewPDFAPI ? (
                <div className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-sky-700">
                  <Spinner /> <span>Loading...</span>
                </div>
              ) : (
                <button
                  onClick={ViewPDF}
                  className="flex items-center gap-1.5 px-3 py-1.5 border border-sky-200 bg-white rounded-md text-xs font-semibold uppercase tracking-wide text-sky-700 hover:bg-sky-50 transition-colors"
                >
                  View PDF
                </button>
              )}

              {isCallingDownloadPDFAPI ? (
                <div className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-sky-700">
                  <Spinner /> <span>Loading...</span>
                </div>
              ) : (
                <button
                  onClick={downloadPDF}
                  className="flex items-center gap-1.5 px-3 py-1.5 border border-sky-200 bg-white rounded-md text-xs font-semibold uppercase tracking-wide text-sky-700 hover:bg-sky-50 transition-colors"
                >
                  Download PDF
                </button>
              )}

              {showDownloadExcelLoader ? (
                <div className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-sky-700">
                  <Spinner /> <span>Please wait</span>
                </div>
              ) : (
                <button
                  onClick={handleDownload}
                  className="flex items-center gap-1.5 px-3 py-1.5 border border-emerald-200 bg-white rounded-md text-xs font-semibold uppercase tracking-wide text-emerald-700 hover:bg-emerald-50 transition-colors"
                >
                  Download Excel
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-1 min-h-0">
          {/* Main content: Filters + Table */}
          <div className="flex-1 flex flex-col min-w-0 p-4 gap-3 overflow-hidden">
            {/* Filter Bar */}
            <div className="border border-violet-100 bg-violet-50/30 rounded-md p-3 shrink-0">
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div className="flex gap-3 items-end">
                  <div className="w-[220px]">
                    <SelectInput
                      value={currentSelectedModeObject?.value || ""}
                      onChange={(e) => {
                        const chip = salesModes.find(
                          (m) => m.value === e.target.value
                        );
                        if (chip) handleChipClick(chip);
                      }}
                    >
                      {salesModes.map((chip, index) => (
                        <option key={index} value={chip.value}>
                          {chip.label}
                        </option>
                      ))}
                    </SelectInput>
                  </div>

                  <div>
                    <TextInput
                      type="date"
                      value={dateTo}
                      max={max_date_today}
                      onChange={(e) => setDateTo(e.target.value)}
                    />
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={resetFilters}
                    className="px-3 py-1.5 border border-rose-200 bg-white rounded-md text-xs font-semibold uppercase tracking-wide text-rose-500 hover:bg-rose-50 transition-colors"
                  >
                    Reset
                  </button>
                  <button
                    onClick={() => setIsFilterPanelVisible((prev) => !prev)}
                    className="px-3 py-1.5 bg-gradient-to-r from-violet-400 to-indigo-400 text-white rounded-md text-xs font-semibold uppercase tracking-wide hover:from-violet-500 hover:to-indigo-500 transition-colors shadow-sm"
                  >
                    Filters
                  </button>
                </div>
              </div>

              {/* Expanded Filters */}
              {isFilterPanelVisible && (
                <div className="mt-4 pt-4 border-t border-violet-100 flex flex-wrap gap-4 items-end">
                  <div className="w-[180px]">
                    <FieldLabel>Location</FieldLabel>
                    <SelectInput
                      value={location_value_to_paas_to_api}
                      onChange={(e) =>
                        setLocation_value_to_paas_to_api(e.target.value)
                      }
                    >
                      <option value="-">Select Location</option>
                      {datagetLocationList?.map((group, index) => (
                        <option key={index} value={group.loc_code?.trim()}>
                          {group.DESC?.trim()}
                        </option>
                      ))}
                    </SelectInput>
                  </div>

                  <SimpleAutocomplete
                    label="Select Group"
                    options={datagetgroup}
                    getOptionLabel={(option) => option?.DESCRIPTION || ""}
                    loading={loadinggetgroup}
                    value={
                      datagetgroup?.find(
                        (g) => g["GRP CODE"] === currentSelectedGroup
                      ) || null
                    }
                    onChange={(newValue) =>
                      setCurrentSelectedGroup(
                        newValue ? newValue["GRP CODE"] : "-"
                      )
                    }
                  />

                  <SimpleAutocomplete
                    label="Select SubGroup"
                    options={datagetSubgroup}
                    getOptionLabel={(option) => option?.DESCRIPTION || ""}
                    loading={loadinggetSubgroup}
                    value={
                      datagetSubgroup?.find(
                        (g) => g["GRP CODE"] === currentSelectedSubGroup
                      ) || null
                    }
                    onChange={(newValue) =>
                      setCurrentSelectedSubGroup(
                        newValue ? newValue["GRP CODE"] : "-"
                      )
                    }
                  />

                  <SimpleAutocomplete
                    label="Select Category"
                    options={datagetCategory}
                    getOptionLabel={(option) => option?.DESCRIPTION || ""}
                    loading={loadinggetCategory}
                    value={
                      datagetCategory?.find(
                        (g) => g["GRP CODE"] === currentSelectedCategory
                      ) || null
                    }
                    onChange={(newValue) =>
                      setCurrentSelectedCategory(
                        newValue ? newValue["GRP CODE"] : "-"
                      )
                    }
                  />

                  <SimpleAutocomplete
                    label="Select SubCategory"
                    options={datagetSubCategory}
                    getOptionLabel={(option) => option?.DESCRIPTION || ""}
                    loading={loadinggetSubCategory}
                    value={
                      datagetSubCategory?.find(
                        (g) => g["GRP CODE"] === currentSelectedSubCategory
                      ) || null
                    }
                    onChange={(newValue) =>
                      setCurrentSelectedSubCategory(
                        newValue ? newValue["GRP CODE"] : "-"
                      )
                    }
                  />

                  <div>
                    <FieldLabel>Filter Mode</FieldLabel>
                    <div className="flex items-center gap-3 h-[30px]">
                      {["All", "Plus", "Minus", "NonZero"].map((status) => (
                        <label
                          key={status}
                          className="flex items-center gap-1.5 text-xs text-gray-700 cursor-pointer"
                        >
                          <input
                            type="radio"
                            name="stock-filter"
                            value={status}
                            checked={current_statusOfReturnCheckbox === status}
                            onChange={(e) =>
                              setCurrentStatusOfReturnCheckbox(e.target.value)
                            }
                            className="accent-violet-500"
                          />
                          {status === "Plus"
                            ? "Plus Only"
                            : status === "Minus"
                            ? "Minus Only"
                            : status === "NonZero"
                            ? "Non Zero"
                            : status}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="w-full mt-2">
                    <button
                      onClick={() => {
                        if (current_page_number === 1) {
                          callStockReportAPI(1);
                        } else {
                          set_current_page_number(1);
                        }
                      }}
                      className="px-4 py-1.5 bg-indigo-600 text-white rounded-md text-xs font-semibold uppercase tracking-wide hover:bg-indigo-700 transition-colors"
                    >
                      Show Data based on filters
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Table Section */}
            <div className="flex-1 min-h-0 flex flex-col border border-violet-100 rounded-md overflow-hidden">
              <div
                ref={scrollContainerRef}
                onScroll={handleScroll}
                className="flex-1 min-h-0 overflow-y-auto"
              >
                {CurrentTableComponent ? (
                  <CurrentTableComponent
                    datagetTotalSeperateAPI={datagetTotalSeperateAPI}
                    rows={paginatedRows}
                    loading={loadinggetSalesOrderReportAPI}
                    mode={currentSelectedModeObject.value}
                    footerTotalKeys={keysToTotal}
                    groupSalesThreeLevels={groupSalesThreeLevels}
                    descriptiveColumnKey={descriptiveColumnKey}
                    totalColumnsCount={totalColumnsCount}
                    hasMore={hasMore}
                    loadNextPage={loadNextPage}
                  />
                ) : (
                  <div className="text-center py-10">
                    <p className="text-xs font-semibold text-violet-300 uppercase tracking-wide">
                      Please select a report mode.
                    </p>
                  </div>
                )}
              </div>

              {/* Pagination Controls Bar */}
              <div className="px-4 py-2 border-t border-violet-100 flex items-center justify-between bg-white">
                {/* LEFT: Row & Page Count Status */}
                <div className="flex items-center gap-4 text-xs font-semibold text-gray-600">
                  <span>Current Rows: {filteredRows?.length}</span>
                  <span>
                    Total Rows:{" "}
                    {datagetTotalSeperateAPI
                      ? datagetTotalSeperateAPI[0]?.TotalRows
                      : ""}
                  </span>
                  <span>
                    Total Pages:{" "}
                    {datagetTotalSeperateAPI
                      ? datagetTotalSeperateAPI[0]?.LastPage
                      : ""}
                  </span>
                </div>

                {/* RIGHT: Prev / Page Number / Next Buttons */}
                <div className="flex items-center space-x-2">
                  <button
                    className="bg-violet-500 hover:bg-violet-600 disabled:opacity-50 text-xs text-white font-semibold px-2 py-1 rounded transition-colors"
                    onClick={() =>
                      set_current_page_number((prev) =>
                        prev - 1 >= 1 ? prev - 1 : 1
                      )
                    }
                    disabled={current_page_number === 1}
                  >
                    Prev
                  </button>
                  <span className="text-xs text-violet-700 font-medium px-2">
                    Page {current_page_number}
                  </span>
                  <button
                    onClick={() => set_current_page_number((prev) => prev + 1)}
                    className="bg-violet-500 hover:bg-violet-600 disabled:opacity-50 text-xs text-white font-semibold px-2 py-1 rounded transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>

              {/* Report Footer */}
              <div className="border-t-2 border-violet-200" />
              <div className="border-t border-violet-100" />
              {/* <div className="px-4 py-2.5 flex items-center justify-between bg-gradient-to-r from-violet-50 to-sky-50">
                <p className="text-xs font-semibold text-teal-700">
                  Total Rows Found: {filteredRows.length}
                </p>
                <p className="text-xs text-gray-400">
                  Generated:{" "}
                  {new Date().toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}
                </p>
              </div> */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockReport;
